import { isUncertainChoice, type ScriptEvidence } from "@kensio/pinyinjs";

import { element } from "./dom.js";
import {
  type NotableChoice,
  notableChoices,
  type WrittenScript,
} from "./script-decisions.js";
import { alternativesNote } from "./script-output.js";
import { panelName } from "./script-panels.js";

/**
 * The account of what the conversions had to decide, on the page.
 *
 * Which decisions there were is `script-decisions.ts`. This is the part a
 * visitor reads: what became what, why, and — where the panels disagreed —
 * which of them a row is about. It is the thing that makes this converter
 * different from a lookup table, so it is written to be read rather than
 * skimmed past.
 */

/** What each kind of evidence is called on the page. */
const evidenceNotes: Readonly<Record<ScriptEvidence, string>> = {
  locked: "Only one form.",
  word: "Settled by the word it is in.",
  reading: "Settled by how it is read here.",
  default: "Nothing settled this one, so the commoner form was taken.",
};

/**
 * The scripts a row is about, where it is not about all of them.
 *
 * Nothing at all for a decision every panel made the same way, which is most
 * of them: saying "in 简体 and 繁體 (Taiwan) and 繁體 (Hong Kong)" on every
 * row would bury the two rows where it actually matters.
 */
function whereNote(
  notable: NotableChoice,
  scripts: number,
): string | undefined {
  return notable.targets.length === scripts
    ? undefined
    : `In ${notable.targets.map((target) => panelName(target)).join(" and ")}:`;
}

/**
 * One row: the character, what it became, and why.
 */
function choiceRow(notable: NotableChoice, scripts: number): Node {
  const { choice } = notable;
  const alternatives = alternativesNote(choice);
  const note = [
    whereNote(notable, scripts),
    evidenceNotes[choice.evidence],
    alternatives === undefined ? undefined : `${alternatives}.`,
  ]
    .filter((part) => part !== undefined)
    .join(" ");

  return element("li", {
    className: isUncertainChoice(choice)
      ? "cb-script-choice cb-script-guess"
      : "cb-script-choice",
    children: [
      element("span", {
        className: "cb-script-forms hanzi",
        text: `${choice.from} → ${choice.to}`,
      }),
      element("span", { className: "cb-script-why", text: note }),
    ],
  });
}

/**
 * Fill the list, and hide the block it sits in when there is nothing to say.
 *
 * The heading goes with the rows rather than staying behind, because
 * "Characters with more than one form" over an empty list reads as a converter
 * that has failed rather than as a text with no ambiguity in it.
 */
export function showChoices(
  panel: HTMLElement,
  list: HTMLElement,
  written: readonly WrittenScript[],
): void {
  const notable = notableChoices(written);

  list.replaceChildren(
    ...notable.map((choice) => choiceRow(choice, written.length)),
  );
  panel.hidden = notable.length === 0;
}
